/**
 * What a Worker status dot means, decided away from the drawing.
 *
 * Pure status string in, pure look out: the tone, the motion, the glyph and the words an
 * accessible reader says, for one Worker's current state. Kept here so every state can be
 * tested without a browser, and so the component stays about drawing.
 *
 * This is status, not identity: the palette says what a Worker is doing, never which
 * provider, model or person the Worker is. Provider and model identity have their own
 * colors elsewhere, and nothing here may borrow them.
 *
 * Only what the platform actually says is named. A status this module does not know falls
 * back to a quiet muted indicator, never to a guess that looks like a known state.
 */

/** The honest kinds of motion a status can have. `settled` plays once and stops. */
export type WorkerStatusMotion = 'orbit' | 'pulse' | 'breathing' | 'attention' | 'connect' | 'settled' | 'none';

/** The shapes a status can draw. Rings carry the motion; glyphs carry the steady meaning. */
export type WorkerStatusShape = 'ring' | 'check' | 'pause' | 'stop';

export interface WorkerStatusLook {
  /** Stable key for tests and data attributes; never a class on its own. */
  readonly key: string;
  /** The words an accessible reader says and the fallback the owner reads. */
  readonly label: string;
  /** Color family, carried as a modifier class, never inline. */
  readonly tone: 'success' | 'thinking' | 'input' | 'approval' | 'error' | 'muted';
  /** Which animation the CSS applies, or `none` for a steady state. */
  readonly motion: WorkerStatusMotion;
  /** What sits inside the ring: nothing, a check, a pause or a stop. */
  readonly shape: WorkerStatusShape;
}

const LOOKS: Readonly<Record<string, WorkerStatusLook>> = {
  working: { key: 'working', label: 'Working', tone: 'success', motion: 'orbit', shape: 'ring' },
  running: { key: 'working', label: 'Working', tone: 'success', motion: 'orbit', shape: 'ring' },
  active: { key: 'working', label: 'Working', tone: 'success', motion: 'orbit', shape: 'ring' },
  thinking: { key: 'thinking', label: 'Thinking', tone: 'thinking', motion: 'pulse', shape: 'ring' },
  processing: { key: 'thinking', label: 'Thinking', tone: 'thinking', motion: 'pulse', shape: 'ring' },
  starting: { key: 'thinking', label: 'Starting', tone: 'thinking', motion: 'pulse', shape: 'ring' },
  waiting_for_input: { key: 'waiting_for_input', label: 'Waiting for input', tone: 'input', motion: 'breathing', shape: 'ring' },
  waiting_for_approval: { key: 'waiting_for_approval', label: 'Waiting for approval', tone: 'approval', motion: 'attention', shape: 'ring' },
  reconnecting: { key: 'reconnecting', label: 'Reconnecting', tone: 'thinking', motion: 'connect', shape: 'ring' },
  error: { key: 'error', label: 'Error', tone: 'error', motion: 'settled', shape: 'ring' },
  failed: { key: 'error', label: 'Failed', tone: 'error', motion: 'settled', shape: 'ring' },
  completed: { key: 'completed', label: 'Completed', tone: 'success', motion: 'settled', shape: 'check' },
  paused: { key: 'paused', label: 'Paused', tone: 'input', motion: 'none', shape: 'pause' },
  idle: { key: 'idle', label: 'Idle', tone: 'muted', motion: 'none', shape: 'ring' },
  offline: { key: 'offline', label: 'Offline', tone: 'muted', motion: 'none', shape: 'ring' },
  stopped: { key: 'stopped', label: 'Stopped', tone: 'muted', motion: 'none', shape: 'stop' },
};

/** What a status the platform does not name falls back to: quiet, muted, honest. */
export const UNKNOWN_STATUS_LOOK: WorkerStatusLook = {
  key: 'unknown', label: 'Unknown status', tone: 'muted', motion: 'none', shape: 'ring',
};

/** One Worker status string in, one truthful look out. Never a guess that flatters. */
export function workerStatusLook(status: string): WorkerStatusLook {
  return LOOKS[status] ?? UNKNOWN_STATUS_LOOK;
}
